
import { GoogleGenAI } from "@google/genai";

// Use updated model names as per latest guidelines
const MODEL_TEXT = "gemini-3-flash-preview"; 
const MODEL_IMAGE = "gemini-2.5-flash-image";
const MODEL_SEARCH = "gemini-3-pro-image-preview"; // Best for tools/search

const getApiKey = (): string => {
  // Strictly enforce process.env.API_KEY as per best practices
  return process.env.API_KEY || "";
};

const getClient = () => {
    const key = getApiKey();
    if (!key) throw new Error("API Key is missing. Please configure process.env.API_KEY in Vercel.");
    return new GoogleGenAI({ apiKey: key });
};

// 1. 技术咨询服务
export const getTechnicalAdvice = async (
  query: string, 
  language: string,
  history: { role: 'user' | 'model'; text: string }[] = []
): Promise<string> => {
  try {
    const ai = getClient();
    
    // Transform history to the format expected by the new SDK's generateContent
    const contents = history.map(h => ({
        role: h.role,
        parts: [{ text: h.text }]
    }));
    
    // Add current query
    contents.push({ role: 'user', parts: [{ text: query }] });

    const response = await ai.models.generateContent({
      model: MODEL_TEXT,
      contents: contents,
      config: {
        systemInstruction: `You are an expert technical sales engineer for MEITAI Instruments. 
        You specialize in industrial instrumentation (Coriolis Flowmeters, Pressure Transmitters, etc.).
        Respond in ${language}. Keep answers concise, professional, and sales-oriented.`,
      }
    });

    return response.text || "No response received.";
  } catch (error) {
    console.error("Gemini API Error:", error);
    return "The assistant is currently offline. Please try again later.";
  }
};

// 2. 翻译服务
export const generateTranslations = async (
  text: string,
  targetLanguages: string[]
): Promise<Record<string, string>> => {
  try {
    const ai = getClient();
    const prompt = `Translate the following text into these languages: ${targetLanguages.join(', ')}. 
    Return ONLY a valid JSON object where keys are the language codes (e.g., "en", "ar", "fr") and values are the translations.
    Text to translate: "${text}"`;

    const response = await ai.models.generateContent({
        model: MODEL_TEXT,
        contents: prompt,
        config: {
            responseMimeType: "application/json"
        }
    });

    const jsonText = response.text;
    if (!jsonText) return {};
    return JSON.parse(jsonText);
  } catch (error) {
    console.error("Translation Error:", error);
    return {};
  }
};

// 3. 产品导入服务
export const importProductFromUrl = async (url: string): Promise<any> => {
  try {
    const ai = getClient();
    
    // Use Search model for extracting info from URLs
    const prompt = `Search for and analyze the product at this URL: ${url}.
    Extract the following information and return it as a JSON object.
    
    REQUIRED JSON STRUCTURE:
    {
      "code": "Product Model Number (or 'N/A')",
      "name": "Product Name (English)",
      "category": "Product Category (English, short)",
      "description": "A short professional marketing description (English)",
      "specs": ["spec1", "spec2", "spec3"],
      "imageUrl": "The main product image URL (absolute path)"
    }
    
    If specific details are missing, infer plausible values for an industrial instrument context.
    Output ONLY the JSON object. Do not wrap in markdown code blocks.
    `;

    const response = await ai.models.generateContent({
        model: MODEL_SEARCH, 
        contents: prompt,
        config: {
            tools: [{ googleSearch: {} }] 
        }
    });

    let jsonText = response.text || "";
    
    // Clean potential markdown blocks
    const match = jsonText.match(/```json\n([\s\S]*?)\n```/) || jsonText.match(/```([\s\S]*?)```/);
    if (match) jsonText = match[1];

    try {
        const parsed = JSON.parse(jsonText);
        if (!parsed || typeof parsed !== 'object') throw new Error("Invalid JSON");
        return parsed;
    } catch (e) {
        console.warn("Failed to parse AI JSON, returning fallback:", e);
        return {
            name: "New Product (Imported)",
            code: "IMPORT-001",
            category: "Industrial",
            description: "Product imported from external source. Please verify details.",
            specs: ["Spec 1", "Spec 2"],
            imageUrl: ""
        };
    }
  } catch (error) {
    console.error("Import Error:", error);
    throw error;
  }
};

// 4. 文章/博客导入服务 (Via URL)
export const importArticleFromUrl = async (url: string): Promise<any> => {
  try {
    const ai = getClient();
    
    const prompt = `Go to this URL: ${url} using the search tool.
    Read the content of the page.
    Summarize it into a JSON object with these fields. Keep original language of the article.
    
    REQUIRED JSON STRUCTURE:
    {
      "title": "Article Title",
      "excerpt": "A short 1-2 sentence summary",
      "content": "The main body content cleaned up (preserve paragraphs)",
      "date": "Publication date in YYYY-MM-DD format (use today if not found)",
      "imageUrl": "The main article image URL (absolute path)"
    }
    `;

    const response = await ai.models.generateContent({
        model: MODEL_SEARCH, 
        contents: prompt,
        config: {
            tools: [{ googleSearch: {} }] 
        }
    });

    let jsonText = response.text || "";
    const match = jsonText.match(/```json\n([\s\S]*?)\n```/) || jsonText.match(/```([\s\S]*?)```/);
    if (match) jsonText = match[1];

    try {
        const parsed = JSON.parse(jsonText);
        return parsed;
    } catch (e) {
        return null;
    }
  } catch (error) {
    console.error("Article Import Error:", error);
    return null;
  }
};

// 5. 文章/博客导入服务 (Via Text Paste)
export const parseArticleFromText = async (text: string): Promise<any> => {
  try {
    const ai = getClient();
    
    const prompt = `Analyze the provided text. It might be raw content or HTML.
    Extract the following into a valid JSON object. Keep the original language of the text.
    
    REQUIRED JSON STRUCTURE:
    {
      "title": "Article Title",
      "excerpt": "A short 1-2 sentence summary",
      "content": "The main body content cleaned up (preserve paragraphs)",
      "date": "Publication date in YYYY-MM-DD format (use today if not found)"
    }
    
    Text Input:
    """
    ${text.slice(0, 25000)}
    """
    `;

    const response = await ai.models.generateContent({
        model: MODEL_TEXT, 
        contents: prompt,
        config: {
            responseMimeType: "application/json"
        }
    });

    return JSON.parse(response.text || "{}");
  } catch (error) {
    console.error("Text Parse Error:", error);
    throw error;
  }
};

// 6. 图片清洗服务
export const cleanProductImage = async (imageUrl: string, productName: string = 'instrument'): Promise<string> => {
  const fallback = "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=1200";
  if (!imageUrl || !imageUrl.startsWith('http')) return fallback;

  try {
    const ai = getClient();

    let browserBlob: Blob | null = null;
    const res = await fetch(imageUrl).catch(() => null);
    if (res && res.ok) browserBlob = await res.blob();
    if (!browserBlob) return imageUrl; 

    const base64Data: string = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const fullString = reader.result as string;
        resolve(fullString?.split(',')[1] ?? "");
      };
      reader.readAsDataURL(browserBlob as Blob);
    });

    if (!base64Data) return imageUrl;

    const response = await ai.models.generateContent({
        model: MODEL_IMAGE,
        contents: {
            parts: [
                {
                    inlineData: {
                        mimeType: browserBlob.type,
                        data: base64Data
                    }
                },
                {
                    text: `Isolate this ${productName} on a clean white background. High quality, professional industrial product photography style. Return the image.`
                }
            ]
        }
    });

    if (response.candidates?.[0]?.content?.parts) {
        for (const part of response.candidates[0].content.parts) {
            if (part.inlineData && part.inlineData.data) {
                return `data:${part.inlineData.mimeType || 'image/png'};base64,${part.inlineData.data}`;
            }
        }
    }
    
    return imageUrl; 
  } catch (error) {
    console.warn("Image clean failed, using original:", error);
    return imageUrl; 
  }
};
